import { useCreateEvent, useDeleteEvent, useUpdateEvent, useUploadFiles } from '@hooks'
import { getErrorMessage } from '@lib/apiError'
import { PostEventType, PutEventType } from '@orbit/api-types'
import { useCallback } from 'react'
import toast from 'react-hot-toast'

/**
 * The itinerary's event writes with their toasts: add (with any pending
 * attachments), a partial update (move, reorder) and delete. The payloads
 * come in built — the create flow's Draft owns that; editing is the edit
 * flow's `useSaveEvent` — so this is wiring
 * and toasts only. The queries refresh through the mutations' own invalidation.
 */
export const useEventMutations = (tripId: string) => {
	const createEvent = useCreateEvent()
	const updateEvent = useUpdateEvent()
	const deleteEvent = useDeleteEvent()
	const uploadFiles = useUploadFiles()

	/** Whether the event was created — a caller holding a draft keeps it when it wasn't. Files that fail after it was still count as created. */
	const addEvent = useCallback(
		async (eventData: PostEventType, pendingFiles?: File[]): Promise<boolean> => {
			let created: { id: number }
			try {
				created = await createEvent.mutateAsync({ tripId, eventData })
			} catch (error) {
				console.error('Failed to create event:', error)
				toast.error(getErrorMessage(error, 'Failed to add event. Please try again.'))
				return false
			}
			if (pendingFiles && pendingFiles.length > 0) {
				try {
					await uploadFiles.mutateAsync({
						tripId,
						files: pendingFiles,
						resourceType: 'TRIP_EVENT',
						resourceId: created.id,
					})
				} catch (error) {
					console.error('Failed to upload event attachments:', error)
					toast.error(`'${eventData.name}' was added, but its attachments failed to upload.`)
					return true
				}
			}
			toast.success(`'${eventData.name}' added successfully!`)
			return true
		},
		[tripId, createEvent, uploadFiles],
	)

	const changeEvent = useCallback(
		async (eventId: number, updates: PutEventType, successMessage?: string) => {
			try {
				await updateEvent.mutateAsync({ tripId, eventId: String(eventId), eventData: updates })
				if (successMessage) toast.success(successMessage)
			} catch (error) {
				console.error('Failed to update event:', error)
				toast.error(getErrorMessage(error, 'Failed to update event. Please try again.'))
			}
		},
		[tripId, updateEvent],
	)

	const removeEvent = useCallback(
		async (eventId: number, eventName: string) => {
			try {
				await deleteEvent.mutateAsync({ tripId, eventId: String(eventId) })
				toast.success(`'${eventName}' deleted successfully!`)
			} catch (error) {
				console.error('Failed to delete event:', error)
				toast.error(getErrorMessage(error, 'Failed to delete event. Please try again.'))
			}
		},
		[tripId, deleteEvent],
	)

	return { addEvent, changeEvent, removeEvent }
}
